import React, { useState } from 'react';
import { Menu, message } from 'antd';
import {
    CopyOutlined,
    FileTextOutlined,
    FlagOutlined,
    FlagFilled,
    EditOutlined,
    OpenAIOutlined
} from '@ant-design/icons';
import { ReactComponent as LabelIcon } from '../../assets/icon/label.svg';
import { getAIPrompt } from '../../utils/aiPrompts';
import { getItemLabelColor, ITEM_LABELS } from '../../constants/itemLabels';
import useTranslation from '../../hooks/useTranslation';

const EllipsisMenu = ({
    quiz,
    mainLanguage,
    translationLanguage,
    showTranslated,
    selectedLabel = null,
    onLabelChange,
}) => {
    const { t } = useTranslation();
    const [reported, setReported] = useState(false);

    const handleLabelMenuClick = (e) => {
        if (e.key === 'clear') {
            onLabelChange?.(null);
        } else {
            const label = ITEM_LABELS.find((item) => item.value === e.key);
            if (label) {
                onLabelChange?.(label.value);
            }
        }
    };

    // Только selectedLabel: откат на quiz.label возвращал бы старый цвет сразу после
    // снятия метки — владелец состояния снаружи, и null у него осмысленный.
    const selectedColor = getItemLabelColor(selectedLabel);

    const handleReportClick = () => {
        setReported(prev => !prev);
    };

    const handleEditClick = () => {
        console.log('Edit clicked');
    };

    const handleCopyClick = () => {
        if (!quiz) return;

        const quizData = {
            question: quiz.question,
            answers: quiz.answers.map(a => ({
                text: a.text,
                translateText: a.translateText || null,
            })),
            translateQuestion: quiz.translateQuestion || null,
        };

        navigator.clipboard.writeText(JSON.stringify(quizData, null, 2))
            .then(() => message.success("Copied to clipboard!"))
            .catch(() => message.error("Failed to copy."));
    };

    const getLangCode = (lang) => {
        const name = lang?.name?.toLowerCase();
        if (!name) return 'en';
        if (name.includes('russian')) return 'ru';
        if (name.includes('english')) return 'en';
        return 'en';
    };

    const getShortLangName = (lang) => lang?.name?.split(' ')[0] || 'Language';

    const getCurrentTextData = () => {
        const question = showTranslated ? quiz.translateQuestion : quiz.question;
        const answers = quiz.answers.map(a =>
            showTranslated ? (a.translateText || '[нет перевода]') : a.text
        );
        return { question, answers };
    };

    const handleAnalyzeClick = (lang) => {
        if (!quiz) return;
        const { question, answers } = getCurrentTextData();

        try {
            const prompt = getAIPrompt('analyze', getLangCode(lang), { question, answers });
            navigator.clipboard.writeText(prompt)
                .then(() => message.success(`Analyze prompt copied for ${getShortLangName(lang)}!`));
        } catch (err) {
            message.error("Error creating prompt.");
            console.error(err);
        }
    };

    const handleAnswerKeyClick = () => {
        if (!quiz) return;
        const lang = showTranslated ? translationLanguage : mainLanguage;
        const { question, answers } = getCurrentTextData();

        try {
            const prompt = getAIPrompt('shortAnswer', getLangCode(lang), { question, answers });
            navigator.clipboard.writeText(prompt)
                .then(() => message.success(`Answer Key copied (${getShortLangName(lang)})!`));
        } catch (err) {
            message.error("Error creating Answer Key.");
            console.error(err);
        }
    };

    const menu = (
        <Menu>
            <Menu.Item
                key="copy"
                icon={<CopyOutlined style={{ fontSize: 16, marginRight: 8 }} />}
                onClick={handleCopyClick}
            >
                Copy
            </Menu.Item>

            <Menu.SubMenu
                key="aiAsk"
                icon={<OpenAIOutlined style={{ fontSize: 16, marginRight: 8 }} />}
                title="AI Ask"
            >
                <Menu.Item
                    key="analyzeOriginal"
                    icon={<CopyOutlined />}
                    onClick={() => handleAnalyzeClick(mainLanguage)}
                >
                    Analyze in {getShortLangName(mainLanguage)}
                </Menu.Item>

                <Menu.Item
                    key="analyzeTranslated"
                    icon={<CopyOutlined />}
                    onClick={() => handleAnalyzeClick(translationLanguage)}
                >
                    Analyze in {getShortLangName(translationLanguage)}
                </Menu.Item>

                <Menu.Divider />

                <Menu.Item
                    key="answerKey"
                    icon={<CopyOutlined />}
                    onClick={handleAnswerKeyClick}
                >
                    Answer Key
                </Menu.Item>
            </Menu.SubMenu>

            <Menu.Item
                key="note"
                icon={<FileTextOutlined style={{ fontSize: 16, marginRight: 8 }} />}
            >
                Note
            </Menu.Item>

            <Menu.Item
                key="edit"
                onClick={handleEditClick}
                icon={<EditOutlined style={{ fontSize: 16, marginRight: 8 }} />}
            >
                Edit
            </Menu.Item>

            <Menu.Item
                key="report"
                onClick={handleReportClick}
                icon={
                    reported ? (
                        <FlagFilled style={{ fontSize: 16, marginRight: 8 }} />
                    ) : (
                        <FlagOutlined style={{ fontSize: 16, marginRight: 8 }} />
                    )
                }
            >
                Report
            </Menu.Item>

            <Menu.SubMenu
                key="label"
                title={
                    <span style={{ display: 'flex', alignItems: 'center' }}>
            <LabelIcon
                style={{
                    fill: selectedColor || "#5b5f62",
                    width: 16,
                    height: 16,
                    marginRight: 8,
                }}
            />
            <span>Label</span>
          </span>
                }
            >
                {ITEM_LABELS.map((item) => (
                    <Menu.Item key={item.value} onClick={handleLabelMenuClick}>
                        <LabelIcon
                            style={{
                                fill: item.color,
                                width: 16,
                                height: 16,
                                marginRight: 8,
                            }}
                        />
                        {t(item.i18nKey)}
                    </Menu.Item>
                ))}
                <Menu.Divider />
                <Menu.Item
                    key="clear"
                    onClick={handleLabelMenuClick}
                    style={{ textAlign: 'center' }}
                >
                    {t('labelMenu.clearColor')}
                </Menu.Item>
            </Menu.SubMenu>
        </Menu>
    );

    return menu;
};

export default EllipsisMenu;
